import {
  Dialog,
  DialogPanel,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition
} from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Student } from '../../pages/StudentRecords';
import { StudentDetails } from '../../redux/Slices/AuthSlice';
import { useAppSelector } from '../../redux/store/hooks';
import { useUpdateStudentProfileMutation } from '../../services/CoOrdinatorApi/coOrdinatorApi';

const EditStudentDialog = ({
  isOpen,
  setIsOpen,
  student
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  student: Student;
}) => {
  const userDetails = useAppSelector(
    state => state.root.auth.studentDetailsById
  );
  const [studentDetails, setStudentDetails] = useState<StudentDetails>(
    {} as StudentDetails
  );
  const [newCompany, setNewCompany] = useState('');

  useEffect(() => {
    setStudentDetails(userDetails);
  }, [userDetails]);

  const [updateStudentProfile, { isLoading }] =
    useUpdateStudentProfileMutation();

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleInputChange = (field: string, value: string | number) => {
    // Convert numeric dropdown values to number
    const processedValue =
      field === 'currentYearOfStudy' ? Number(value) : value;

    setStudentDetails(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  const addCompany = () => {
    if (
      newCompany.trim() &&
      !studentDetails?.interestedCompanies?.includes(newCompany.trim())
    ) {
      setStudentDetails(prev => ({
        ...prev,
        interestedCompanies: [...prev.interestedCompanies, newCompany.trim()]
      }));
      setNewCompany('');
    }
  };

  const removeCompany = (companyToRemove: string) => {
    setStudentDetails(prev => ({
      ...prev,
      interestedCompanies: prev.interestedCompanies.filter(
        company => company !== companyToRemove
      )
    }));
  };

  const handleUpdate = async () => {
    try {
      await updateStudentProfile({
        userId: student?.id,
        body: studentDetails
      }).unwrap();
      toast.success('Updated Successfully');
      setIsOpen(false);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.log(error, 'error13');
    }
  };

  const requiredFields: (keyof StudentDetails)[] = [
    'name',
    'emailId',
    'mobile',
    'college',
    'qualification',
    'department',
    'yearOfCommencement',
    'currentYearOfStudy',
    'yearOfPassing'
  ];

  const isFormValid = requiredFields.every(
    field =>
      studentDetails?.[field] && String(studentDetails[field]).trim() !== ''
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        return null;
      }}
      className='relative z-[9999]'
    >
      <div className='fixed inset-0 bg-black/30' aria-hidden='true' />
      <div className='fixed inset-0 flex items-center justify-center p-4'>
        <DialogPanel className='w-full max-w-2xl space-y-6 rounded-lg border bg-white p-6 shadow-xl'>
          <div className='flex items-center justify-between border-b pb-4'>
            <h2 className='text-2xl font-semibold text-gray-800'>
              Edit Profile
            </h2>
          </div>

          <div className='max-h-96 space-y-4 overflow-y-auto px-4'>
            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Full Name <span className='ml-1 text-[red]'>*</span>
              </label>
              <input
                type='text'
                value={studentDetails?.name}
                onChange={e => handleInputChange('name', e.target.value)}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                placeholder='Enter your full name'
              />
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Email ID <span className='ml-1 text-[red]'>*</span>
              </label>
              <input
                type='email'
                value={studentDetails?.emailId}
                disabled={true}
                onChange={e => handleInputChange('emailId', e.target.value)}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                placeholder='Enter your email address'
              />
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Mobile Number <span className='ml-1 text-[red]'>*</span>
              </label>
              <input
                type='tel'
                value={studentDetails?.mobile}
                onChange={e => handleInputChange('mobile', e.target.value)}
                disabled={true}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                placeholder='Enter your mobile number'
              />
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                College <span className='ml-1 text-[red]'>*</span>
              </label>
              <input
                type='text'
                value={studentDetails?.college}
                onChange={e => handleInputChange('college', e.target.value)}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                placeholder='Enter your college name'
              />
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Qualification <span className='ml-1 text-[red]'>*</span>
              </label>
              <input
                type='text'
                value={studentDetails?.qualification}
                onChange={e => {
                  handleInputChange('qualification', e.target.value);
                }}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                placeholder='Enter your qualification'
                min='2020'
                max='2030'
              />
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Department <span className='ml-1 text-[red]'>*</span>
              </label>
              <input
                type='text'
                value={studentDetails?.department}
                onChange={e => {
                  handleInputChange('department', e.target.value);
                }}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                placeholder='Enter your department'
                min='2020'
                max='2030'
              />
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Year of Commensement <span className='ml-1 text-[red]'>*</span>
              </label>
              <input
                type='number'
                value={studentDetails?.yearOfCommencement}
                onChange={e => {
                  handleInputChange('yearOfCommencement', e.target.value);
                }}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                placeholder='Enter your Year of commencement'
                min='2020'
                max='2030'
              />
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Current Year Of Study <span className='ml-1 text-[red]'>*</span>
              </label>

              <Listbox
                value={Number(studentDetails?.currentYearOfStudy)}
                onChange={value =>
                  handleInputChange('currentYearOfStudy', value)
                }
              >
                <div className='relative mt-1'>
                  <ListboxButton className='w-full rounded-md border border-gray-300 px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500'>
                    {studentDetails?.currentYearOfStudy ?? 'Select Year'}
                  </ListboxButton>

                  <Transition
                    as={Fragment}
                    leave='transition ease-in duration-100'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                  >
                    <ListboxOptions className='absolute mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white py-1 text-base shadow-lg focus:outline-none'>
                      {[1, 2, 3, 4, 5].map(year => (
                        <ListboxOption
                          key={year}
                          value={year}
                          className={({ active }) =>
                            `cursor-pointer select-none px-4 py-2 ${
                              active ? 'bg-blue-100' : 'text-gray-700'
                            }`
                          }
                        >
                          {year}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Year of Passing <span className='ml-1 text-[red]'>*</span>
              </label>
              <input
                type='number'
                value={studentDetails?.yearOfPassing}
                onChange={e => {
                  handleInputChange('yearOfPassing', e.target.value);
                }}
                className='w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                placeholder='Enter your year of passing'
                min='2020'
                max='2030'
              />
            </div>

            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Interested Companies
              </label>
              <div className='mb-2 flex space-x-2'>
                <input
                  type='text'
                  value={newCompany}
                  onChange={e => setNewCompany(e.target.value)}
                  className='flex-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  placeholder='Add a company'
                />
                <button
                  onClick={addCompany}
                  className='rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  Add
                </button>
              </div>
              <div className='flex flex-wrap gap-2'>
                {studentDetails?.interestedCompanies?.map((company, index) => (
                  <span
                    key={company}
                    className='inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800'
                  >
                    {company}
                    <button
                      onClick={() => removeCompany(company)}
                      className='ml-2 text-blue-600 hover:text-blue-800'
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className='flex justify-end space-x-3 border-t pt-4'>
            <button
              onClick={() => handleCancel()}
              className='rounded-md bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500'
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              className={`rounded-md px-4 py-2 text-white focus:outline-none focus:ring-2 
    ${
      isFormValid
        ? 'cursor-pointer bg-blue-500 hover:bg-blue-600 focus:ring-blue-500'
        : 'cursor-not-allowed bg-gray-300'
    }`}
              disabled={!isFormValid || isLoading}
            >
              Update
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default EditStudentDialog;
